import { useEffect, useMemo, useState } from 'react'
import { useAtomValue } from 'jotai'
import useSWR from 'swr'
import { getAddress, isAddress } from 'viem'
import type { NavLive } from '@pancakeswap/uikit'
import { combinedTokenMapFromActiveUrlsAtom } from 'state/lists/hooks'
import { getTokenLogoURLByAddress } from 'utils/getTokenLogoURL'
import { V3_API_URL, fetcher } from 'views/TradingCompetitionV3/config'
import { COMPETITIONS, competitionPath, legacyPath } from 'views/TradingCompetitionV3/routes'
import { V4_API_URL, V4_ENABLED } from 'views/TradingCompetitionV4/config'

/**
 * The live slot of Competitions / Compete (Bez 2026-10-07; a green dot from 2026-10-06): the token logo of each live competition in a
 * green live ring, up to three overlapping and "+N" beyond; the logo area opens the competition (several: the list).
 *
 * No polling and no request of its own. It reads the very SWR keys and fetcher of the competitions list (views/TradingCompetitionV3/
 * List.tsx: the current contract's list, plus the legacy V3 list once V4 is on), so the nav and the list share one cache: one request
 * when the app opens, SWR's revalidate on focus, and the list page's own 30 s refresh while it is open. Logos are the list's own
 * (utils/getTokenLogoURL by address, as views/TokenPages/components/TokenLogo resolves them for the cards); names come from the token
 * lists already in memory. Between fetches the slot follows the clock: a timer fires at the next start or end time in the cached rows.
 */
export type CompetitionListRow = {
  id?: number | string
  projectToken?: string
  phase?: string
  startTime?: number | string
  endTime?: number | string
  /** a row of the previous contract (V3), opened under /legacy */
  legacy?: boolean
}
type ListAnswer = { results?: CompetitionListRow[] }

/** the list page's keys, in its order */
export const competitionListKeys = (): string[] =>
  V4_ENABLED ? [`${V4_API_URL}/competitions?status=all`, `${V3_API_URL}/competitions?status=all`] : [`${V3_API_URL}/competitions?status=all`]

// phases that run on the clock; settled, refunded, cancelled and awaiting_result never count
const RUNNING = new Set(['live', 'upcoming'])

/** live now: a live or upcoming competition whose start has passed and whose end has not (a slightly old cached list stays right) */
const isLiveNow = (r: CompetitionListRow, nowS: number) => RUNNING.has(String(r.phase)) && Number(r.startTime) <= nowS && nowS < Number(r.endTime)

export function isCompetitionLive(rows: CompetitionListRow[] | undefined, nowS: number): boolean {
  return (rows ?? []).some((r) => isLiveNow(r, nowS))
}

/** The competitions live now, in the order the tab shows them: the current contract first, then the one ending soonest, then the newest. */
export function liveCompetitions(rows: CompetitionListRow[] | undefined, nowS: number): CompetitionListRow[] {
  return (rows ?? [])
    .filter((r) => isLiveNow(r, nowS))
    .sort((a, b) => Number(Boolean(a.legacy)) - Number(Boolean(b.legacy)) || Number(a.endTime) - Number(b.endTime) || Number(b.id) - Number(a.id))
}

/** the next moment (unix s) the answer can change: the soonest future start or end of a running competition; null when none */
export function nextLiveChange(rows: CompetitionListRow[] | undefined, nowS: number): number | null {
  const times = (rows ?? [])
    .filter((r) => RUNNING.has(String(r.phase)))
    .flatMap((r) => [Number(r.startTime), Number(r.endTime)])
    .filter((t) => Number.isFinite(t) && t > nowS)
  return times.length ? Math.min(...times) : null
}

/** logos shown on the tab; more live competitions read "+N" */
export const NAV_LIVE_MAX_LOGOS = 3

type Translate = (key: string, data?: Record<string, string | number>) => string
const plain: Translate = (key, data) => key.replace(/%(\w+)%/g, (_, k) => String(data?.[k] ?? ''))

/**
 * The tab's live slot from the list rows: logos of the first three live competitions, "+N" for the rest, and where the logo area goes
 * (one live: that competition, current or legacy; several: the list). Nothing live: no logos, no link (the slot stays reserved).
 */
export function navLive(
  rows: CompetitionListRow[] | undefined,
  nowS: number,
  symbolOf: (address: string) => string | undefined = () => undefined,
  t: Translate = plain,
): NavLive {
  const live = liveCompetitions(rows, nowS)
  if (live.length === 0) return { logos: [], more: 0 }
  const nameOf = (r: CompetitionListRow) => (r.projectToken && symbolOf(r.projectToken)) || `#${r.id}`
  const logos = live.slice(0, NAV_LIVE_MAX_LOGOS).map((r) => {
    const address = r.projectToken && isAddress(r.projectToken) ? getAddress(r.projectToken) : null
    return {
      key: `${r.legacy ? 'l' : 'c'}${r.id}`,
      // the same source the competition cards use for an address (the app's own CADINU / CBON files, else Trust Wallet's assets)
      src: address ? getTokenLogoURLByAddress(address, 56) : null,
      label: nameOf(r),
    }
  })
  if (live.length === 1) {
    const [only] = live
    return {
      logos,
      more: 0,
      href: only.legacy ? legacyPath(only.id as number) : competitionPath(only.id as number),
      ariaLabel: t('Live competition: %name%', { name: nameOf(only) }),
    }
  }
  return { logos, more: live.length - logos.length, href: COMPETITIONS, ariaLabel: t('%n% live competitions', { n: live.length }) }
}

// setTimeout overflows above ~24.8 days; a later change is picked up by the next fetch anyway
const MAX_WAIT_MS = 24 * 3600 * 1000

/** The competitions list's cached rows (legacy ones flagged) and the clock the live state follows. */
export function useCompetitionRows(): { rows: CompetitionListRow[]; nowS: number } {
  const [curKey, oldKey] = competitionListKeys()
  // same key + fetcher as the list page; no refreshInterval here (the list page polls while it is open)
  const { data: cur } = useSWR<ListAnswer>(curKey, fetcher, { shouldRetryOnError: false })
  const { data: old } = useSWR<ListAnswer>(oldKey ?? null, fetcher, { shouldRetryOnError: false })
  const rows = useMemo(() => [...(cur?.results ?? []), ...(old?.results ?? []).map((r) => ({ ...r, legacy: true }))], [cur, old])
  const [nowS, setNowS] = useState(() => Math.floor(Date.now() / 1000))
  useEffect(() => {
    const now = Math.floor(Date.now() / 1000)
    if (now !== nowS) setNowS(now)
    const next = nextLiveChange(rows, now)
    if (next === null) return undefined
    const timer = setTimeout(() => setNowS(Math.floor(Date.now() / 1000)), Math.min((next - now) * 1000 + 250, MAX_WAIT_MS))
    return () => clearTimeout(timer)
  }, [rows, nowS])
  return { rows, nowS }
}

/** The Competitions tab's live slot (navLive over the cached list, names from the token lists in memory). */
export function useCompetitionNavLive(t?: Translate): NavLive {
  const { rows, nowS } = useCompetitionRows()
  const tokens = useAtomValue(combinedTokenMapFromActiveUrlsAtom)
  return useMemo(() => {
    const bsc = (tokens as Record<number, Record<string, { token: { symbol?: string } }>>)[56] ?? {}
    const symbolOf = (a: string) => (isAddress(a) ? bsc[getAddress(a)]?.token.symbol : undefined)
    return navLive(rows, nowS, symbolOf, t)
  }, [rows, nowS, tokens, t])
}
